var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { bind, execute } from "@hyper-hyper-space/hhs3_rdb_lang";
import { boundWithAuthor, hasExplicitBy, hasExplicitByAst, isAuthRetryBound, isBindAuthorRequiredFailure, isBindAuthorRetryStatement, labelForKeyId, resolveAuthorForBoundFailure, resolveAuthorsForAddMember, resolveAuthorsForAlterSchema, } from "./authz_suggest.js";
const AUTH_FAILURE_PATTERNS = [
    'does not satisfy ALLOW',
    'canObserve predicate rejected',
    'canDeploy predicate rejected',
    'must be authored',
    'rowAuthor = $author',
];
function isAuthRelatedFailure(diagnostics) {
    return diagnostics.some((d) => d.code === 'VALIDATION_REJECTED'
        && AUTH_FAILURE_PATTERNS.some((pattern) => d.message.includes(pattern)));
}
function formatOpKind(kind) {
    switch (kind) {
        case 'insert': return 'Insert';
        case 'update': return 'Update';
        case 'delete': return 'Delete';
        case 'update-ref': return 'Update ref';
        case 'update-schema': return 'Update schema';
    }
}
function formatBindOpKind(stmt) {
    switch (stmt.kind) {
        case 'alter-schema': return 'Alter schema';
        case 'add-member': return stmt.member === 'schema' ? 'Add schema' : 'Add tablegroup';
    }
}
export function bindContextWithAuthor(context, author) {
    return Object.assign(Object.assign({}, context), { currentAuthor: () => Promise.resolve(author), resolveVariable: (name) => name === 'me' || name === 'author'
            ? Promise.resolve(author)
            : context.resolveVariable(name) });
}
function identityForSignRetry(session, resolution, auth) {
    return __awaiter(this, void 0, void 0, function* () {
        if (resolution.identity !== undefined)
            return resolution.identity;
        const locked = resolution.locked;
        if (locked === undefined)
            return undefined;
        if ((auth === null || auth === void 0 ? void 0 : auth.unlockIdentity) === undefined)
            return undefined;
        return auth.unlockIdentity(locked.label);
    });
}
function signRetryAuthorLabel(session, resolution) {
    if (resolution.identity !== undefined)
        return labelForKeyId(session, resolution.identity.keyId);
    if (resolution.locked !== undefined)
        return `$${resolution.locked.label}`;
    return undefined;
}
export function tryAuthSignRetry(session, bound, diagnostics, auth) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (((_a = auth === null || auth === void 0 ? void 0 : auth.canPrompt) === null || _a === void 0 ? void 0 : _a.call(auth)) !== true)
            return undefined;
        if (session.keyVault === undefined)
            return undefined;
        if (!isAuthRelatedFailure(diagnostics))
            return undefined;
        if (!isAuthRetryBound(bound))
            return undefined;
        if (hasExplicitBy(bound))
            return undefined;
        const resolution = yield resolveAuthorForBoundFailure(session, bound);
        const authorLabel = signRetryAuthorLabel(session, resolution);
        if (authorLabel === undefined)
            return undefined;
        if (auth.confirmSignRetry === undefined)
            return undefined;
        if (!(yield auth.confirmSignRetry(authorLabel, formatOpKind(bound.kind))))
            return undefined;
        const identity = yield identityForSignRetry(session, resolution, auth);
        if (identity === undefined)
            return undefined;
        const signed = boundWithAuthor(bound, identity);
        const retried = yield execute(signed);
        if (!retried.ok)
            return undefined;
        return { result: retried.value, bound: signed };
    });
}
export function tryBindAuthorRetry(session, statement, diagnostics, context, auth) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (((_a = auth === null || auth === void 0 ? void 0 : auth.canPrompt) === null || _a === void 0 ? void 0 : _a.call(auth)) !== true)
            return undefined;
        if (session.keyVault === undefined)
            return undefined;
        if (!isBindAuthorRequiredFailure(diagnostics))
            return undefined;
        if (!isBindAuthorRetryStatement(statement))
            return undefined;
        if (hasExplicitByAst(statement))
            return undefined;
        let resolution;
        try {
            resolution = statement.kind === 'alter-schema'
                ? yield resolveAuthorsForAlterSchema(session, statement, context)
                : yield resolveAuthorsForAddMember(session, statement, context);
        }
        catch (_b) {
            return undefined;
        }
        const authorLabel = signRetryAuthorLabel(session, resolution);
        if (authorLabel === undefined)
            return undefined;
        if (auth.confirmSignRetry === undefined)
            return undefined;
        if (!(yield auth.confirmSignRetry(authorLabel, formatBindOpKind(statement))))
            return undefined;
        const identity = yield identityForSignRetry(session, resolution, auth);
        if (identity === undefined)
            return undefined;
        const bindStatement = statementForBindRetry(statement, resolution, session);
        const rebound = yield bind(bindStatement, bindContextWithAuthor(context, identity));
        if (!rebound.ok)
            return undefined;
        return rebound.value;
    });
}
function statementForBindRetry(statement, resolution, session) {
    var _a, _b, _c;
    if (statement.kind !== 'add-member')
        return statement;
    const label = resolution.identity !== undefined
        ? (_b = (_a = session.keyVault) === null || _a === void 0 ? void 0 : _a.list().find((key) => key.keyId === resolution.identity.keyId)) === null || _b === void 0 ? void 0 : _b.label
        : (_c = resolution.locked) === null || _c === void 0 ? void 0 : _c.label;
    if (label === undefined)
        return statement;
    return Object.assign(Object.assign({}, statement), { author: { kind: 'variable', name: label, span: statement.span } });
}
